/**
 * CustomerHistoryTimeline — unified, hierarchy-aware history timeline.
 *
 * Replaces the old flat "Activity" row list (which dumped job-created /
 * fault-created / stage-change micro-events all at the same visual weight, so
 * a busy customer read as noise). The new model:
 *
 *  - JOBS are the HERO nodes — big cards that open into the full record the
 *    office actually wants: AI customer + technical summary, field log,
 *    photos, reasoning capture, materials, gear, notes.
 *  - FAULTS / DEALS are MEDIUM cards — symptom / title up top, full
 *    investigation history / activity log folded INSIDE the expansion (not
 *    spilled onto the timeline as separate rows).
 *  - SERVICES / CALLS are SMALL cards.
 *  - LIFECYCLE / system micro-events are a hideable TINY layer (off by
 *    default) so the spine stays clean but power users can still drill in.
 *
 * "Cascading" — related records thread together. A job carries a chip back to
 * the fault or deal it came from; a fault/deal carries chips to the job(s) it
 * spawned. Clicking a chip scroll-snaps + pulse-highlights the linked node so
 * you can walk a story (call → deal → job → fault → return job) without losing
 * your place on the spine.
 *
 * Pure-render component: the parent passes already-loaded arrays (the customer
 * modal already fetches them). State here is purely UI — expand/collapse,
 * filter chips, search, focus-scroll.
 */

import { getLocaleSync } from '@/utils/locale';
import { photoCategoryLabel } from '@/utils/photoCategory';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, ChevronDown, ChevronRight, Wrench, Zap, Briefcase, Wifi, Phone,
  History, Clock, MapPin, User, Calendar, CheckCircle2, FileText,
  Brain, Image as ImageIcon, MessageSquare, Package, Cpu,
  ArrowUpRight, Link2, DollarSign, Shield, Flag, Activity, ExternalLink,
  CornerDownRight, Layers,
} from 'lucide-react';
import { Job, CustomerService, CallLog, JobCompletionReasoning, Note } from '@/types/schema';
import type { ServiceRequest } from '@/types/service-request.types';
import { SERVICE_REQUEST_STAGE_LABELS } from '@/types/service-request.types';
import type { Deal } from '@/types/theo-workflow.types';
import { DEAL_STAGE_LABELS } from '@/types/theo-workflow.types';
import type { Fault } from '@/types/fault.types';
import { FAULT_STAGE_LABELS, migrateFaultStage } from '@/types/fault.types';
import { ENTRY_TYPE_LABELS, ENTRY_TYPE_COLORS } from '@/services/ai-entry-classifier.service';
import { getOnSiteMinutes } from '@/services/job-state.service';
import type { CustomerActivityLogEntry } from '@/services/aggregate-customer.service';
import { MarkdownDisplay } from '@/components/MarkdownDisplay';
import { NoteReplies } from '@/components/notes/NoteReplies';
import type { MentionableUser } from '@/types/mention.types';
import { buildNoteThreads } from '@/utils/note-threads';
import { useTeam } from '@/hooks/useTeam';
import { formatCurrency } from '@/utils/pricing';
import { addressSuburb, composeSiteAddress } from '@/utils/address';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NodeKind = 'job' | 'fault' | 'deal' | 'service' | 'service_request' | 'call' | 'note' | 'lifecycle';
type Tier = 'hero' | 'medium' | 'small' | 'tiny';

interface TimelineNode {
  id: string;
  kind: NodeKind;
  tier: Tier;
  ms: number;
  iso: string;
  job?: Job;
  fault?: Fault;
  deal?: Deal;
  service?: CustomerService;
  serviceRequest?: ServiceRequest;
  call?: CallLog;
  note?: Note;
  /** Replies to `note`, oldest first (kind='note' only). */
  noteReplies?: Note[];
  lifecycle?: CustomerActivityLogEntry;
  /** Free-text search haystack. */
  search: string;
}

export interface CustomerHistoryTimelineProps {
  jobs: Job[];
  faults: Fault[];
  deals: Deal[];
  services: CustomerService[];
  /** Customer-initiated service change requests (speed/plan changes etc.). */
  serviceRequests?: ServiceRequest[];
  calls: CallLog[];
  /** Unified notes — customer notes + rolled-up notes from linked entities. */
  notes?: Note[];
  auditEntries?: CustomerActivityLogEntry[];
  /** 'fullscreen' relaxes max-widths and uses a multi-column hero layout. */
  variant?: 'inline' | 'fullscreen';
  /** Called when the timeline wants the host modal to close (e.g. on navigate). */
  onRequestHostClose?: () => void;
  /**
   * Rendered under the "No history yet" copy — a genuinely empty customer is
   * exactly where an operator wants to start something. Only shown when there
   * are no nodes at all, never for a filtered-to-nothing view (the fix there
   * is Clear filters, not a new record).
   */
  emptyStateAction?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function toMs(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as { toDate?: () => Date; toMillis?: () => number; seconds?: number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.toDate === 'function') return o.toDate().getTime();
    if (typeof o.seconds === 'number') return o.seconds * 1000;
  }
  return 0;
}

function toIso(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const ms = toMs(value);
  return ms ? new Date(ms).toISOString() : '';
}

/** Job date: completedAt → date+time → createdAt. */
function jobMs(j: Job): number {
  const c = toMs(j.completedAt);
  if (c) return c;
  if (j.date) {
    const [y, m, d] = j.date.split('-').map(Number);
    if (y && m && d) {
      let hh = 0, mm = 0;
      if (j.time) {
        const [h, mi] = j.time.split(':').map(Number);
        if (Number.isFinite(h)) hh = h;
        if (Number.isFinite(mi)) mm = mi;
      }
      return new Date(y, m - 1, d, hh, mm).getTime();
    }
  }
  return toMs(j.createdAt);
}

function fmtDateTime(ms: number): string {
  if (!ms) return 'No date';
  return new Date(ms).toLocaleString(getLocaleSync(), {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDate(ms: number): string {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtTimeOnly(iso: string): string {
  const ms = toMs(iso);
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString(getLocaleSync(), { hour: 'numeric', minute: '2-digit' });
}

function timeAgo(ms: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days > 60) return fmtDate(ms);
  if (days > 0) return `${days}d ago`;
  if (hrs > 0) return `${hrs}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

function monthLabel(ms: number): string {
  if (!ms) return 'Undated';
  return new Date(ms).toLocaleDateString(getLocaleSync(), { month: 'long', year: 'numeric' });
}

function onSiteDisplay(j: Job): string {
  const mins = getOnSiteMinutes(j);
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

function firstLine(text?: string, max = 140): string | undefined {
  if (!text) return undefined;
  const t = text.replace(/\s+/g, ' ').trim();
  if (!t) return undefined;
  return t.length > max ? t.slice(0, max) + '…' : t;
}

// ---------------------------------------------------------------------------
// Per-kind visual config
// ---------------------------------------------------------------------------

const JOB_TYPE_META: Record<string, { dot: string; accent: string; chip: string; text: string }> = {
  Install:     { dot: 'bg-purple-500',  accent: 'border-l-purple-500',  chip: 'bg-purple-500/15 text-purple-300', text: 'text-purple-400' },
  Fault:       { dot: 'bg-rose-500',     accent: 'border-l-rose-500',    chip: 'bg-rose-500/15 text-rose-300',     text: 'text-rose-400' },
  'Pre-Site':  { dot: 'bg-amber-500',    accent: 'border-l-amber-500',   chip: 'bg-amber-500/15 text-amber-300',   text: 'text-amber-400' },
  Task:        { dot: 'bg-slate-400',    accent: 'border-l-slate-400',   chip: 'bg-slate-500/15 text-slate-300',   text: 'text-slate-400' },
  Maintenance: { dot: 'bg-blue-500',     accent: 'border-l-blue-500',    chip: 'bg-blue-500/15 text-blue-300',     text: 'text-blue-400' },
};
function jobMeta(type?: string) { return JOB_TYPE_META[type || 'Task'] || JOB_TYPE_META.Task; }

const KIND_META: Record<NodeKind, { label: string; plural: string; icon: typeof Wrench; dot: string; text: string; chipActive: string }> = {
  job:       { label: 'Job',       plural: 'Jobs',     icon: Wrench,    dot: 'bg-blue-500',    text: 'text-blue-400',    chipActive: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  fault:     { label: 'Fault',     plural: 'Faults',   icon: Zap,       dot: 'bg-rose-500',    text: 'text-rose-400',    chipActive: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
  deal:      { label: 'Deal',      plural: 'Deals',    icon: Briefcase, dot: 'bg-emerald-500', text: 'text-emerald-400', chipActive: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  service:   { label: 'Service',   plural: 'Services', icon: Wifi,      dot: 'bg-purple-500',  text: 'text-purple-400',  chipActive: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  service_request: { label: 'Request', plural: 'Requests', icon: FileText, dot: 'bg-indigo-500', text: 'text-indigo-400', chipActive: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40' },
  call:      { label: 'Call',      plural: 'Calls',    icon: Phone,     dot: 'bg-sky-500',     text: 'text-sky-400',     chipActive: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
  note:      { label: 'Note',      plural: 'Notes',    icon: MessageSquare, dot: 'bg-amber-500', text: 'text-amber-400', chipActive: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  lifecycle: { label: 'Event',     plural: 'Events',   icon: History,   dot: 'bg-cyan-500',    text: 'text-cyan-400',    chipActive: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' },
};

const SEVERITY_CHIP: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const DEAL_STAGE_CHIP: Record<string, string> = {
  INTAKE: 'bg-sky-500/20 text-sky-400', PRESITE: 'bg-amber-500/20 text-amber-400',
  QUOTING: 'bg-blue-500/20 text-blue-400', PROPOSAL_SENT: 'bg-indigo-500/20 text-indigo-400',
  WON: 'bg-emerald-500/20 text-emerald-400', PROVISIONING: 'bg-purple-500/20 text-purple-400',
  SCHEDULING: 'bg-cyan-500/20 text-cyan-400', REVIEW: 'bg-teal-500/20 text-teal-400',
  BILLING: 'bg-orange-500/20 text-orange-400', COMPLETED: 'bg-green-500/20 text-green-400',
  LOST: 'bg-red-500/20 text-red-400',
};

const JOB_STATUS_CHIP: Record<string, string> = {
  Completed: 'bg-emerald-500/20 text-emerald-400', 'In Progress': 'bg-blue-500/20 text-blue-400',
  Scheduled: 'bg-brand-secondary/20 text-brand-secondary', Unscheduled: 'bg-slate-500/20 text-slate-400',
  Cancelled: 'bg-red-500/20 text-red-400',
};

// ---------------------------------------------------------------------------
// Tiny shared bits
// ---------------------------------------------------------------------------

function Chip({ className = '', children }: { className?: string; children: React.ReactNode }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${className}`}>{children}</span>;
}

/** Collapsible sub-section used inside an expanded card. */
function Disc({
  title, icon: Icon, badge, defaultOpen = false, accent = 'text-theme-muted', children,
}: {
  title: string; icon: typeof FileText; badge?: string; defaultOpen?: boolean; accent?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-theme-soft rounded-lg overflow-hidden bg-surface-hover/40">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-surface-hover transition-colors">
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${accent}`} />
          <span className="text-xs font-semibold text-theme-secondary">{title}</span>
          {badge && <span className="text-[9px] bg-surface-hover text-theme-muted px-1.5 py-0.5 rounded-full">{badge}</span>}
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-theme-muted" /> : <ChevronRight className="w-3.5 h-3.5 text-theme-muted" />}
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
}

/** Clickable chip that threads to a related node on the spine. */
function LinkChip({ icon: Icon, label, onClick, className = '' }: { icon: typeof Link2; label: string; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors hover:brightness-125 ${className}`}
    >
      <Icon className="w-3 h-3" />
      <span className="truncate max-w-[160px]">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Field log (shared by job + fault expansions)
// ---------------------------------------------------------------------------

function FieldLogList({ entries }: { entries: { timestamp: string; text: string; techName?: string; entryType?: keyof typeof ENTRY_TYPE_LABELS; editedAt?: string }[] }) {
  const sorted = [...entries].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));
  return (
    <div className="relative pl-4 space-y-2">
      <div className="absolute left-[5px] top-1 bottom-1 w-px bg-blue-400/20" />
      {sorted.map((entry, i) => (
        <div key={i} className="relative">
          <div className="absolute -left-4 top-1 w-2 h-2 rounded-full bg-blue-400/60 ring-2 ring-surface-card" />
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-theme-muted">{fmtTimeOnly(entry.timestamp)}</span>
            {entry.techName && <span className="text-[10px] text-theme-muted">· {entry.techName}</span>}
            {entry.entryType && ENTRY_TYPE_COLORS[entry.entryType] && (
              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${ENTRY_TYPE_COLORS[entry.entryType].bg} ${ENTRY_TYPE_COLORS[entry.entryType].text} ${ENTRY_TYPE_COLORS[entry.entryType].border}`}>
                {ENTRY_TYPE_LABELS[entry.entryType]}
              </span>
            )}
          </div>
          <p className="text-xs text-theme-primary mt-0.5 whitespace-pre-wrap">{entry.text}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// JOB hero card
// ---------------------------------------------------------------------------

function JobHero({
  job, expanded, onToggle, onFocusNode, links, fullscreen, onNavigate, team,
}: {
  job: Job; expanded: boolean; onToggle: () => void;
  onFocusNode: (id: string) => void;
  links: { parentFaultId?: string; parentDealId?: string; parentFaultLabel?: string; parentDealLabel?: string };
  fullscreen: boolean; onNavigate: (path: string) => void;
  team: { id?: string; name?: string }[];
}) {
  const meta = jobMeta(job.type);
  const techNames = (job as { techNames?: string[] }).techNames;
  const techDisplay = techNames?.join(', ') || (job as { techName?: string }).techName
    || (job.techIds || []).map((id) => team.find((t) => t.id === id)?.name).filter(Boolean).join(', ') || '';
  const fieldLog = job.fieldLog || [];
  const photos = job.photos || [];
  const onSite = onSiteDisplay(job);
  const checklistItems = job.installChecklist?.items?.length ? job.installChecklist.items : job.checkpoints;
  const checkDone = checklistItems?.filter((i: { checked?: boolean; completed?: boolean }) => i.checked || i.completed).length || 0;
  const checkTotal = checklistItems?.length || 0;
  // The tech's own words lead the preview. This used to open with
  // `customerSummary` — AI copy written for the post-job email, which reads
  // like marketing next to a real service history. It's billing
  // correspondence only now (ai-summary.service.ts) and isn't shown here.
  const preview = firstLine(job.completionNote) || firstLine(job.notes);

  const reasoning = job.completionReasoning;
  const reasoningItems = reasoning && !reasoning.skipped ? ([
    ['symptomProgression', 'Symptom progression'], ['ruledOutPaths', 'Ruled-out paths'],
    ['whatFixedIt', 'What fixed it'], ['keyDecisions', 'Key decisions'], ['siteSurprises', 'Site surprises'],
    ['customerContext', 'Customer context'], ['scopeRationale', 'Scope rationale'], ['risksConcerns', 'Risks / concerns'],
    ['siteQuirks', 'Site quirks'], ['whatAndWhy', 'What you did and why'], ['anythingNotable', 'Anything notable'],
  ] as [keyof JobCompletionReasoning, string][]).map(([k, label]) => ({ label, value: reasoning[k] as string | undefined }))
    .filter((x) => typeof x.value === 'string' && x.value.trim()) : [];

  const plannedMaterials = job.usedMaterials?.length ? job.usedMaterials : job.plannedMaterials;
  const gear = job.additionalGearUsed || [];

  return (
    <div className={`bg-surface-card border border-theme rounded-2xl border-l-4 ${meta.accent} overflow-hidden shadow-sm transition-all`}>
      {/* Hero header (always visible) */}
      <button onClick={onToggle} className="w-full text-left px-4 py-3.5 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl ${meta.chip} flex items-center justify-center shrink-0`}>
            <Wrench className={`w-4 h-4 ${meta.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-widest ${meta.text}`}>{job.type || 'Job'}</span>
              {job.status && <Chip className={JOB_STATUS_CHIP[job.status] || 'bg-surface-hover text-theme-muted'}>{job.status}</Chip>}
              {onSite && <Chip className="bg-emerald-500/15 text-emerald-400"><Clock className="w-2.5 h-2.5" />{onSite}</Chip>}
              {job.sos && <Chip className="bg-red-500/20 text-red-400"><Shield className="w-2.5 h-2.5" />SOS</Chip>}
              {job.flagged && <Chip className="bg-amber-500/20 text-amber-400"><Flag className="w-2.5 h-2.5" />Flag</Chip>}
            </div>
            <h3 className="text-[15px] font-bold text-theme-primary mt-1 truncate">
              {job.type || 'Job'}{addressSuburb(job) ? ` — ${addressSuburb(job)}` : ''}
            </h3>
            {/* Facts row */}
            <div className="flex items-center gap-3 mt-1 text-[11px] text-theme-muted flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(jobMs(job))}{job.time ? ` · ${job.time}` : ''}</span>
              {techDisplay && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{techDisplay}</span>}
              {composeSiteAddress(job) && <span className="inline-flex items-center gap-1 truncate max-w-[200px]"><MapPin className="w-3 h-3" />{composeSiteAddress(job)}</span>}
            </div>
            {preview && !expanded && (
              <p className="text-xs text-theme-tertiary mt-1.5 line-clamp-2 leading-relaxed">{preview}</p>
            )}
            {/* Cross-link + content indicators */}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {links.parentFaultLabel && (
                <LinkChip icon={Zap} label={`From fault: ${links.parentFaultLabel}`} onClick={() => links.parentFaultId && onFocusNode(`fault-${links.parentFaultId}`)} className="bg-rose-500/10 text-rose-400 border-rose-500/30" />
              )}
              {links.parentDealLabel && (
                <LinkChip icon={Briefcase} label={`From deal: ${links.parentDealLabel}`} onClick={() => links.parentDealId && onFocusNode(`deal-${links.parentDealId}`)} className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30" />
              )}
              {fieldLog.length > 0 && <Chip className="bg-blue-500/10 text-blue-400"><MessageSquare className="w-2.5 h-2.5" />{fieldLog.length}</Chip>}
              {photos.length > 0 && <Chip className="bg-purple-500/10 text-purple-400"><ImageIcon className="w-2.5 h-2.5" />{photos.length}</Chip>}
              {checkTotal > 0 && <Chip className={checkDone === checkTotal ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>{checkDone}/{checkTotal} ✓</Chip>}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-theme-muted shrink-0 mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded detail (cascade) */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2.5 border-t border-theme-soft pt-3">
          {/* Completion block */}
          {job.status === 'Completed' && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400"><CheckCircle2 className="w-4 h-4" />Completed</span>
                {onSite && <span className="text-xs font-bold text-emerald-300 tabular-nums">{onSite} on site</span>}
              </div>
              {job.completedAt && (
                <p className="text-[11px] text-emerald-400/70 mt-0.5">
                  {fmtDateTime(toMs(job.completedAt))}
                  {job.duration > 0 && getOnSiteMinutes(job) > 0 && (() => {
                    const over = getOnSiteMinutes(job) - job.duration;
                    return over >= 30 ? <span className="text-amber-400 ml-1">+{over}m over schedule</span> : null;
                  })()}
                </p>
              )}
              {job.completionNote && (
                <div className="mt-2 pt-2 border-t border-emerald-500/15">
                  <p className="text-[10px] font-bold text-emerald-400/80 uppercase tracking-widest mb-1">Completion note</p>
                  <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{job.completionNote}</p>
                </div>
              )}
            </div>
          )}

          {/* AI write-up — Fault / Maintenance visits with a field log only. */}
          {job.aiSummary?.technicalReport && (
            <Disc title="AI write-up of the log" icon={FileText} accent="text-brand-secondary">
              <div className="text-xs text-theme-primary">
                <MarkdownDisplay content={job.aiSummary.technicalReport} compact />
              </div>
            </Disc>
          )}

          {/* Reasoning capture */}
          {reasoning && (reasoningItems.length > 0 || reasoning.skipped) && (
            <Disc title="Reasoning Capture" icon={Brain} accent="text-indigo-400" badge={reasoning.skipped ? 'Skipped' : `${reasoningItems.length}`}>
              {reasoning.skipped ? (
                <p className="text-xs text-amber-400">Skipped by tech — {reasoning.skipReason || 'no reason given'}</p>
              ) : (
                <div className="space-y-2">
                  {reasoningItems.map((item) => (
                    <div key={item.label} className="bg-surface-hover rounded-lg p-2.5 border border-theme-soft">
                      <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest mb-1">{item.label}</p>
                      <p className="text-xs text-theme-primary whitespace-pre-wrap">{item.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </Disc>
          )}

          {/* Field log */}
          {fieldLog.length > 0 && (
            <Disc title="Field Log" icon={MessageSquare} accent="text-blue-400" badge={`${fieldLog.length}`} defaultOpen={job.status === 'Completed' && fieldLog.length <= 6}>
              <FieldLogList entries={fieldLog} />
            </Disc>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <Disc title="Photos" icon={ImageIcon} accent="text-purple-400" badge={`${photos.length}`}>
              <div className={`grid gap-2 ${fullscreen ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {photos.map((p, i) => (
                  <a key={p.id || i} href={p.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="relative block aspect-square rounded-lg overflow-hidden bg-surface-hover border border-theme-soft hover:border-purple-400/40 transition-all">
                    <img src={p.url} alt={p.caption || `Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    {photoCategoryLabel(p.category) && (
                      <span className="absolute top-1 left-1 bg-black/70 text-white text-[8px] font-bold uppercase tracking-wide px-1 py-0.5 rounded">{photoCategoryLabel(p.category)}</span>
                    )}
                  </a>
                ))}
              </div>
            </Disc>
          )}

          {/* Materials + gear */}
          {((plannedMaterials && plannedMaterials.length > 0) || gear.length > 0) && (
            <Disc title="Materials & Gear" icon={Package} accent="text-cyan-400" badge={`${(plannedMaterials?.length || 0) + gear.length}`}>
              <div className="space-y-1.5">
                {plannedMaterials?.map((m, i) => (
                  <div key={m.id || i} className="flex items-center justify-between text-xs">
                    <span className="text-theme-secondary">{m.name}{(m as { serialNumber?: string }).serialNumber ? <span className="text-cyan-400 ml-1.5 font-mono text-[10px]">S/N {(m as { serialNumber?: string }).serialNumber}</span> : ''}</span>
                    <span className="text-theme-muted tabular-nums">×{m.quantity}{m.unit ? ` ${m.unit}` : ''}</span>
                  </div>
                ))}
                {gear.map((g, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-theme-secondary">{g.name} <span className="text-[10px] text-amber-400">(extra · {g.reason})</span></span>
                    <span className="text-theme-muted tabular-nums">×{g.quantity}</span>
                  </div>
                ))}
              </div>
            </Disc>
          )}

          {/* Notes */}
          {(job.notes || (job.jobNotes && job.jobNotes.length > 0)) && (
            <Disc title="Notes" icon={FileText} badge={job.jobNotes?.length ? `${job.jobNotes.length}` : undefined}>
              <div className="space-y-2">
                {job.notes && <p className="text-xs text-theme-primary whitespace-pre-wrap">{job.notes}</p>}
                {job.jobNotes?.map((n) => (
                  <div key={n.id} className="bg-surface-hover rounded-lg p-2 border border-theme-soft">
                    <p className="text-xs text-theme-primary whitespace-pre-wrap">{n.content}</p>
                    <p className="text-[10px] text-theme-muted mt-1">{n.createdByName} · {fmtDate(toMs(n.createdAt))}</p>
                  </div>
                ))}
              </div>
            </Disc>
          )}

          {/* Deep link */}
          <div className="flex items-center gap-2 pt-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(`/home/jobs?openId=${job.id}`); }}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />Open in Jobs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAULT card
// ---------------------------------------------------------------------------

function FaultCard({
  fault, expanded, onToggle, onFocusNode, childJobs, fullscreen, onNavigate,
}: {
  fault: Fault; expanded: boolean; onToggle: () => void;
  onFocusNode: (id: string) => void;
  childJobs: { id: string; label: string }[];
  fullscreen: boolean; onNavigate: (path: string) => void;
}) {
  const stage = migrateFaultStage(fault.stage);
  const statusLabel = fault.archived
    ? (fault.resolutionType === 'deployed' ? (fault.deploymentJobCompleted ? 'Resolved (deployed)' : 'Deployed') : (fault.resolutionType || 'Closed').replace(/_/g, ' '))
    : (FAULT_STAGE_LABELS[stage] || fault.stage);
  const invHistory = fault.investigationHistory || [];
  const clientUpdates = fault.clientUpdates || [];
  const preview = firstLine(fault.resolutionNotes) || firstLine(fault.intakeNotes);

  return (
    <div className="bg-surface-card border border-theme rounded-xl border-l-2 border-l-rose-500/60 overflow-hidden">
      <button onClick={onToggle} className="w-full text-left px-3.5 py-3 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-rose-500/15 flex items-center justify-center shrink-0">
            <Zap className="w-3.5 h-3.5 text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Chip className="bg-rose-500/15 text-rose-300">Fault</Chip>
              {fault.severity && <Chip className={SEVERITY_CHIP[fault.severity] || 'bg-surface-hover text-theme-muted'}>{fault.severity}</Chip>}
              <Chip className="bg-surface-hover text-theme-muted">{statusLabel}</Chip>
              {fault.repeatFlag && <Chip className="bg-orange-500/20 text-orange-400">Repeat</Chip>}
            </div>
            <p className="text-sm font-semibold text-theme-primary mt-1 truncate">{fault.symptom || 'Fault'}</p>
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-theme-muted flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(toMs(fault.createdAt))}</span>
              {fault.serviceType && <span className="inline-flex items-center gap-1"><Wifi className="w-3 h-3" />{fault.serviceType}{fault.servicePlanName ? ` — ${fault.servicePlanName}` : ''}</span>}
              {fault.deviceInfo?.type && <span className="inline-flex items-center gap-1"><Cpu className="w-3 h-3" />{fault.deviceInfo.type}{fault.deviceInfo.model ? ` ${fault.deviceInfo.model}` : ''}</span>}
            </div>
            {preview && !expanded && <p className="text-xs text-theme-tertiary mt-1 line-clamp-1">{preview}</p>}
            {childJobs.length > 0 && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {childJobs.map((cj) => (
                  <LinkChip key={cj.id} icon={CornerDownRight} label={cj.label} onClick={() => onFocusNode(`job-${cj.id}`)} className="bg-blue-500/10 text-blue-400 border-blue-500/30" />
                ))}
              </div>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-theme-muted shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-2.5 border-t border-theme-soft pt-3">
          {fault.intakeNotes && (
            <div>
              <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest mb-1">Intake</p>
              <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{fault.intakeNotes}</p>
            </div>
          )}
          {fault.resolutionNotes && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5">
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Resolution</p>
              <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{fault.resolutionNotes}</p>
            </div>
          )}
          {invHistory.length > 0 && (
            <Disc title="Investigation History" icon={Activity} accent="text-rose-400" badge={`${invHistory.length}`} defaultOpen={invHistory.length <= 6}>
              <div className="relative pl-4 space-y-2">
                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-rose-400/20" />
                {[...invHistory].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp)).map((e, i) => (
                  <div key={e.id || i} className="relative">
                    <div className="absolute -left-4 top-1 w-2 h-2 rounded-full bg-rose-400/60 ring-2 ring-surface-card" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-bold text-theme-muted">{fmtTimeOnly(e.timestamp)}</span>
                      <span className="text-xs font-semibold text-theme-secondary">{e.action}</span>
                    </div>
                    {e.details && <p className="text-xs text-theme-primary mt-0.5 whitespace-pre-wrap">{e.details}</p>}
                    {e.author && <p className="text-[10px] text-theme-muted mt-0.5">{e.author}</p>}
                  </div>
                ))}
              </div>
            </Disc>
          )}
          {clientUpdates.length > 0 && (
            <Disc title="Customer Updates" icon={Phone} accent="text-sky-400" badge={`${clientUpdates.length}`}>
              <div className="space-y-1.5">
                {[...clientUpdates].sort((a, b) => toMs(b.timestamp) - toMs(a.timestamp)).map((u, i) => (
                  <div key={u.id || i} className="bg-surface-hover rounded-lg p-2 border border-theme-soft">
                    <div className="flex items-center gap-1.5">
                      <Chip className="bg-sky-500/15 text-sky-400">{u.method}</Chip>
                      <span className="text-[10px] text-theme-muted">{fmtDate(toMs(u.timestamp))}</span>
                    </div>
                    <p className="text-xs text-theme-primary mt-1 whitespace-pre-wrap">{u.content}</p>
                  </div>
                ))}
              </div>
            </Disc>
          )}
          {fault.costs && fault.costs.totalCost > 0 && (
            <div className="flex items-center gap-3 text-[11px] text-theme-muted">
              <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3" />Total {formatCurrency(fault.costs.totalCost)}</span>
              {fault.costs.labourMinutes > 0 && <span>{Math.round(fault.costs.labourMinutes / 6) / 10}h labour</span>}
            </div>
          )}
          {childJobs.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
              <span className="text-[10px] text-theme-muted inline-flex items-center gap-1"><Layers className="w-3 h-3" />Spawned:</span>
              {childJobs.map((cj) => (
                <LinkChip key={cj.id} icon={Wrench} label={cj.label} onClick={() => onFocusNode(`job-${cj.id}`)} className="bg-blue-500/10 text-blue-400 border-blue-500/30" />
              ))}
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(`/home/faults?openId=${fault.id}`); }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-400 hover:text-rose-300 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />Open in Faults
          </button>
          {fullscreen && <div className="h-0" />}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DEAL card
// ---------------------------------------------------------------------------

function DealCard({
  deal, expanded, onToggle, onFocusNode, childJobs, onNavigate,
}: {
  deal: Deal; expanded: boolean; onToggle: () => void;
  onFocusNode: (id: string) => void;
  childJobs: { id: string; label: string }[];
  onNavigate: (path: string) => void;
}) {
  const title = deal.title || (deal.selectedServices || []).join(', ') || 'Deal';
  const stageLabel = DEAL_STAGE_LABELS[deal.stage as keyof typeof DEAL_STAGE_LABELS] || deal.stage;
  const activity = deal.activityLog || [];
  const notes = deal.dealNotes || [];

  return (
    <div className="bg-surface-card border border-theme rounded-xl border-l-2 border-l-emerald-500/60 overflow-hidden">
      <button onClick={onToggle} className="w-full text-left px-3.5 py-3 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Briefcase className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Chip className="bg-emerald-500/15 text-emerald-300">Deal</Chip>
              <Chip className={DEAL_STAGE_CHIP[deal.stage] || 'bg-surface-hover text-theme-muted'}>{stageLabel}</Chip>
              {!!deal.value && <Chip className="bg-surface-hover text-theme-secondary">{formatCurrency(Number(deal.value))}</Chip>}
              {!!deal.monthlyRecurring && <Chip className="bg-emerald-500/10 text-emerald-400">{formatCurrency(Number(deal.monthlyRecurring))}/mo</Chip>}
            </div>
            <p className="text-sm font-semibold text-theme-primary mt-1 truncate">{title}</p>
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-theme-muted flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(toMs(deal.createdAt))}</span>
              {(deal.selectedServices || []).length > 0 && <span className="truncate max-w-[220px]">{(deal.selectedServices || []).join(', ')}</span>}
            </div>
            {childJobs.length > 0 && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {childJobs.map((cj) => (
                  <LinkChip key={cj.id} icon={CornerDownRight} label={cj.label} onClick={() => onFocusNode(`job-${cj.id}`)} className="bg-blue-500/10 text-blue-400 border-blue-500/30" />
                ))}
              </div>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-theme-muted shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-2.5 border-t border-theme-soft pt-3">
          {notes.length > 0 && (
            <Disc title="Deal Notes" icon={FileText} accent="text-emerald-400" badge={`${notes.length}`} defaultOpen>
              <div className="space-y-2">
                {[...notes].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)).map((n) => (
                  <div key={n.id} className="bg-surface-hover rounded-lg p-2 border border-theme-soft">
                    <div className="text-xs text-theme-primary"><MarkdownDisplay content={n.content} compact /></div>
                    <p className="text-[10px] text-theme-muted mt-1">{n.createdByName} · {fmtDate(toMs(n.createdAt))}</p>
                  </div>
                ))}
              </div>
            </Disc>
          )}
          {activity.length > 0 && (
            <Disc title="Deal Activity" icon={Activity} accent="text-emerald-400" badge={`${activity.length}`}>
              <div className="relative pl-4 space-y-2">
                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-emerald-400/20" />
                {[...activity].sort((a, b) => toMs(b.timestamp) - toMs(a.timestamp)).slice(0, 40).map((e, i) => (
                  <div key={e.id || i} className="relative">
                    <div className="absolute -left-4 top-1 w-2 h-2 rounded-full bg-emerald-400/60 ring-2 ring-surface-card" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-bold text-theme-muted">{fmtTimeOnly(e.timestamp)}</span>
                      <span className="text-xs font-semibold text-theme-secondary">{e.action}</span>
                    </div>
                    {e.details && <p className="text-xs text-theme-primary mt-0.5">{e.details}</p>}
                    {e.actorName && <p className="text-[10px] text-theme-muted">{e.actorName}</p>}
                  </div>
                ))}
              </div>
            </Disc>
          )}
          {childJobs.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
              <span className="text-[10px] text-theme-muted inline-flex items-center gap-1"><Layers className="w-3 h-3" />Jobs:</span>
              {childJobs.map((cj) => (
                <LinkChip key={cj.id} icon={Wrench} label={cj.label} onClick={() => onFocusNode(`job-${cj.id}`)} className="bg-blue-500/10 text-blue-400 border-blue-500/30" />
              ))}
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(`/home/deals/${deal.id}`); }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <ArrowUpRight className="w-3 h-3" />Open deal workspace
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SERVICE + CALL small cards
// ---------------------------------------------------------------------------

function ServiceCard({ service, expanded, onToggle }: { service: CustomerService; expanded: boolean; onToggle: () => void }) {
  const statusChip = service.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400'
    : service.status === 'Cancelled' ? 'bg-red-500/20 text-red-400'
    : service.status === 'Suspended' ? 'bg-amber-500/20 text-amber-400'
    : 'bg-surface-hover text-theme-muted';
  const meta = (service.metadata || {}) as Record<string, unknown>;
  const metaRows = [
    ['Kit number', meta.kitNumber], ['Account', meta.starlinkAccountNumber], ['Account email', meta.starlinkAccountEmail], ['Model', meta.starlinkModel],
  ].filter(([, v]) => !!v) as [string, string][];

  return (
    <div className="bg-surface-card border border-theme rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full text-left px-3.5 py-2.5 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-purple-500/15 flex items-center justify-center shrink-0"><Wifi className="w-3 h-3 text-purple-400" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-theme-primary truncate">{service.serviceType}{service.planName ? ` — ${service.planName}` : ''}</span>
              <Chip className={statusChip}>{service.status}</Chip>
              {!!service.monthlyPrice && <Chip className="bg-surface-hover text-theme-secondary">${service.monthlyPrice}/mo</Chip>}
            </div>
            <p className="text-[11px] text-theme-muted mt-0.5">{fmtDate(toMs(service.createdAt))}{service.address ? ` · ${service.address}` : ''}</p>
          </div>
          {(metaRows.length > 0 || service.address) && <ChevronDown className={`w-3.5 h-3.5 text-theme-muted shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />}
        </div>
      </button>
      {expanded && (metaRows.length > 0 || service.address) && (
        <div className="px-3.5 pb-3 border-t border-theme-soft pt-2 space-y-1">
          {service.address && <div className="flex justify-between text-xs"><span className="text-theme-muted">Address</span><span className="text-theme-secondary text-right">{service.address}</span></div>}
          {metaRows.map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs"><span className="text-theme-muted">{k}</span><span className="text-theme-secondary font-mono text-right">{v}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function CallCard({ call, expanded, onToggle }: { call: CallLog; expanded: boolean; onToggle: () => void }) {
  const typeChip: Record<string, string> = {
    sales: 'bg-green-500/20 text-green-400', fault: 'bg-rose-500/20 text-rose-400',
    enquiry: 'bg-blue-500/20 text-blue-400', billing: 'bg-purple-500/20 text-purple-400',
  };
  const preview = firstLine(call.notes, 100);
  return (
    <div className="bg-surface-card border border-theme rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full text-left px-3.5 py-2.5 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-sky-500/15 flex items-center justify-center shrink-0"><Phone className="w-3 h-3 text-sky-400" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Chip className={typeChip[call.callType] || 'bg-surface-hover text-theme-muted'}>{call.callType}</Chip>
              {call.urgency && call.urgency !== 'normal' && <Chip className="bg-amber-500/15 text-amber-400">{call.urgency}</Chip>}
              <span className="text-[11px] text-theme-muted">{fmtDate(toMs(call.timestamp))}</span>
            </div>
            {preview && !expanded && <p className="text-xs text-theme-tertiary mt-0.5 line-clamp-1">{preview}</p>}
          </div>
          {call.notes && <ChevronDown className={`w-3.5 h-3.5 text-theme-muted shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />}
        </div>
      </button>
      {expanded && call.notes && (
        <div className="px-3.5 pb-3 border-t border-theme-soft pt-2">
          <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{call.notes}</p>
          {call.loggedByName && <p className="text-[10px] text-theme-muted mt-1.5">Logged by {call.loggedByName}</p>}
          {call.requiresFollowUp && call.followUpDate && <p className="text-[10px] text-amber-400 mt-0.5">Follow-up: {call.followUpDate}{call.followUpNotes ? ` — ${call.followUpNotes}` : ''}</p>}
        </div>
      )}
    </div>
  );
}

function ServiceRequestCard({ request, expanded, onToggle, onNavigate }: {
  request: ServiceRequest; expanded: boolean; onToggle: () => void; onNavigate: (path: string) => void;
}) {
  const stageLabel = SERVICE_REQUEST_STAGE_LABELS[request.stage] || request.stage;
  const preview = firstLine(request.description || '', 100);
  return (
    <div className="bg-surface-card border border-theme rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full text-left px-3.5 py-2.5 hover:bg-surface-hover/50 transition-colors">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0"><FileText className="w-3 h-3 text-indigo-400" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-theme-primary truncate">{request.title}</span>
              <Chip className="bg-indigo-500/15 text-indigo-400">{stageLabel}</Chip>
              <span className="text-[11px] text-theme-muted">{fmtDate(toMs(request.createdAt))}</span>
            </div>
            {preview && !expanded && <p className="text-xs text-theme-tertiary mt-0.5 line-clamp-1">{preview}</p>}
          </div>
          <ChevronDown className={`w-3.5 h-3.5 text-theme-muted shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {expanded && (
        <div className="px-3.5 pb-3 border-t border-theme-soft pt-2 space-y-1.5">
          {request.description && (
            <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{request.description}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-theme-muted">
            <span>{request.requestType}</span>
            {request.serviceType && <span>· {request.serviceType}</span>}
            {(request.linkedJobIds?.length ?? 0) > 0 && <span>· {request.linkedJobIds!.length} linked job{request.linkedJobIds!.length > 1 ? 's' : ''}</span>}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(`/home/service-requests?openId=${request.id}`); }}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-400 hover:text-indigo-300"
          >
            Open request <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function LifecycleRow({ entry }: { entry: CustomerActivityLogEntry }) {
  return (
    <div className="flex items-start gap-2 py-1 pl-0.5">
      <History className="w-3 h-3 text-cyan-400/70 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-theme-secondary leading-snug">{entry.summary}</p>
        <p className="text-[10px] text-theme-muted">{entry.actorName || entry.actorId} · {fmtDate(toMs(entry.timestamp))}</p>
      </div>
    </div>
  );
}

function NoteCard({
  note,
  replies,
  availableUsers,
}: {
  note: Note;
  replies: Note[];
  availableUsers: MentionableUser[];
}) {
  // Where the note hangs off — a customer note reads plainly; a rolled-up
  // note from a linked deal/fault/SR carries a small origin chip.
  const originLabel = note.entityType === 'customer' ? null : KIND_META[
    note.entityType === 'service_request' ? 'service_request' : (note.entityType as NodeKind)
  ]?.label;
  return (
    <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-3.5 h-3.5 text-amber-400 shrink-0" />
        <span className="text-[11px] font-semibold text-theme-primary">{note.createdByName || 'Office'}</span>
        {note.pinned && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Pinned</span>}
        {originLabel && (
          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-surface-hover text-theme-tertiary">on {originLabel}</span>
        )}
        <span className="text-[10px] text-theme-muted ml-auto">{fmtDateTime(toMs(note.createdAt))}</span>
      </div>
      <p className="text-xs text-theme-primary whitespace-pre-wrap leading-relaxed">{note.content}</p>

      <NoteReplies
        parent={note}
        replies={replies}
        availableUsers={availableUsers}
        entityTitle={`${ENTITY_TITLE_PREFIX[note.entityType] ?? 'Note'} · ${note.createdByName || 'Office'}`}
        source={note.source}
      />
    </div>
  );
}

/** Headline prefix for reply mention notifications, per parent entity. */
const ENTITY_TITLE_PREFIX: Record<string, string> = {
  customer: 'Customer',
  deal: 'Deal',
  fault: 'Fault',
  service_request: 'Service request',
  job: 'Job',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CustomerHistoryTimeline({
  jobs, faults, deals, services, serviceRequests = [], calls, notes = [], auditEntries = [],
  variant = 'inline', onRequestHostClose, emptyStateAction,
}: CustomerHistoryTimelineProps) {
  const navigate = useNavigate();
  const fullscreen = variant === 'fullscreen';
  const { team } = useTeam(); // one listener for the whole timeline (not per-job)

  // Team list for the reply composer's @tag picker. Dual-access users appear
  // as Office — mentions route to their office persona.
  const mentionUsers: MentionableUser[] = useMemo(
    () =>
      team
        .filter((m) => m.active !== false)
        .map((m) => ({
          id: m.id,
          name: m.name,
          role: m.dualAccess ? 'Office' : m.role,
          isActive: m.active,
        })),
    [team],
  );

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Set<NodeKind>>(new Set());
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleNavigate = useCallback((path: string) => {
    onRequestHostClose?.();
    navigate(path);
  }, [navigate, onRequestHostClose]);

  // Cross-link maps -------------------------------------------------------
  const { jobsByFault, jobsByDeal, faultLabelById, dealLabelById } = useMemo(() => {
    const jobLabel = (j: Job) => `${j.type || 'Job'}${j.date ? ` · ${fmtDate(jobMs(j))}` : ''}`;
    const jobsByFault = new Map<string, { id: string; label: string }[]>();
    const jobsByDeal = new Map<string, { id: string; label: string }[]>();
    const faultLabelById = new Map<string, string>();
    const dealLabelById = new Map<string, string>();
    faults.forEach((f) => { if (f.id) faultLabelById.set(f.id, firstLine(f.symptom, 36) || 'Fault'); });
    deals.forEach((d) => { if (d.id) dealLabelById.set(d.id, firstLine(d.title || (d.selectedServices || []).join(', '), 36) || 'Deal'); });
    jobs.forEach((j) => {
      if (!j.id) return;
      const lbl = { id: j.id, label: jobLabel(j) };
      const fId = j.faultId || j.briefingHistory?.faultId;
      if (fId) { const arr = jobsByFault.get(fId) || []; arr.push(lbl); jobsByFault.set(fId, arr); }
      if (j.dealId) { const arr = jobsByDeal.get(j.dealId) || []; arr.push(lbl); jobsByDeal.set(j.dealId, arr); }
    });
    // Also bind faults to their deployment job even if the job lacks faultId.
    faults.forEach((f) => {
      if (f.id && f.deploymentJobId) {
        const j = jobs.find((x) => x.id === f.deploymentJobId);
        if (j && j.id) {
          const arr = jobsByFault.get(f.id) || [];
          if (!arr.some((a) => a.id === j.id)) { arr.push({ id: j.id, label: jobLabel(j) }); jobsByFault.set(f.id, arr); }
        }
      }
    });
    return { jobsByFault, jobsByDeal, faultLabelById, dealLabelById };
  }, [jobs, faults, deals]);

  // Build nodes -----------------------------------------------------------
  const nodes = useMemo<TimelineNode[]>(() => {
    const out: TimelineNode[] = [];
    jobs.forEach((j) => j.id && out.push({
      id: `job-${j.id}`, kind: 'job', tier: 'hero', ms: jobMs(j), iso: toIso(j.completedAt || j.createdAt), job: j,
      search: `${j.type} ${j.client} ${j.loc} ${j.address} ${j.notes} ${j.completionNote} ${(j as { techName?: string }).techName} ${(j.techIds || []).join(' ')}`.toLowerCase(),
    }));
    faults.forEach((f) => f.id && out.push({
      id: `fault-${f.id}`, kind: 'fault', tier: 'medium', ms: toMs(f.createdAt), iso: toIso(f.createdAt), fault: f,
      search: `${f.symptom} ${f.serviceType} ${f.servicePlanName} ${f.intakeNotes} ${f.resolutionNotes} ${f.severity}`.toLowerCase(),
    }));
    deals.forEach((d) => d.id && out.push({
      id: `deal-${d.id}`, kind: 'deal', tier: 'medium', ms: toMs(d.createdAt), iso: toIso(d.createdAt), deal: d,
      search: `${d.title} ${(d.selectedServices || []).join(' ')} ${d.stage} ${d.value}`.toLowerCase(),
    }));
    services.forEach((s) => s.id && out.push({
      id: `service-${s.id}`, kind: 'service', tier: 'small', ms: toMs(s.createdAt), iso: toIso(s.createdAt), service: s,
      search: `${s.serviceType} ${s.planName} ${s.status} ${s.address}`.toLowerCase(),
    }));
    serviceRequests.forEach((r) => r.id && out.push({
      id: `service_request-${r.id}`, kind: 'service_request', tier: 'small', ms: toMs(r.createdAt), iso: toIso(r.createdAt), serviceRequest: r,
      search: `${r.title} ${r.requestType} ${r.stage} ${r.description || ''} ${r.serviceType || ''}`.toLowerCase(),
    }));
    calls.forEach((c) => c.id && out.push({
      id: `call-${c.id}`, kind: 'call', tier: 'small', ms: toMs(c.timestamp), iso: toIso(c.timestamp), call: c,
      search: `${c.callType} ${c.notes} ${c.urgency}`.toLowerCase(),
    }));
    // Threaded: a reply renders inside its parent's card, not as its own node
    // in the timeline (which would scatter a conversation across the history).
    buildNoteThreads(notes).forEach(({ note: nt, replies }) => nt.id && out.push({
      id: `note-${nt.id}`, kind: 'note', tier: 'small', ms: toMs(nt.createdAt), iso: toIso(nt.createdAt), note: nt,
      noteReplies: replies,
      search: `${nt.content} ${nt.createdByName} ${nt.entityType} ${replies.map((r) => r.content).join(' ')}`.toLowerCase(),
    }));
    if (showLifecycle) {
      auditEntries.forEach((a) => out.push({
        id: `lifecycle-${a.id || `${a.sourceId}-${a.timestamp}`}`, kind: 'lifecycle', tier: 'tiny', ms: toMs(a.timestamp), iso: a.timestamp, lifecycle: a,
        search: `${a.summary} ${a.actorName}`.toLowerCase(),
      }));
    }
    return out;
  }, [jobs, faults, deals, services, serviceRequests, calls, notes, auditEntries, showLifecycle]);

  // Counts (pre-filter) for chip badges
  const counts = useMemo(() => ({
    job: jobs.length, fault: faults.length, deal: deals.length,
    service: services.length, service_request: serviceRequests.length,
    call: calls.length, note: notes.length, lifecycle: auditEntries.length,
  }), [jobs, faults, deals, services, serviceRequests, calls, notes, auditEntries]);

  // Filter + sort
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nodes
      .filter((n) => filters.size === 0 || filters.has(n.kind))
      .filter((n) => !q || n.search.includes(q))
      .sort((a, b) => b.ms - a.ms);
  }, [nodes, filters, search]);

  // Group by month bucket (preserving newest-first order)
  const groups = useMemo(() => {
    const out: { label: string; nodes: TimelineNode[] }[] = [];
    let cur: { label: string; nodes: TimelineNode[] } | null = null;
    for (const n of filtered) {
      const lbl = monthLabel(n.ms);
      if (!cur || cur.label !== lbl) { cur = { label: lbl, nodes: [] }; out.push(cur); }
      cur.nodes.push(n);
    }
    return out;
  }, [filtered]);

  // Focus-scroll handler — expand + scroll + pulse the target node.
  const focusNode = useCallback((nodeId: string) => {
    setExpanded((prev) => { const next = new Set(prev); next.add(nodeId); return next; });
    setFocusId(nodeId);
  }, []);

  useEffect(() => {
    if (!focusId) return;
    // If the target is hidden by an active filter/search, clear them so the
    // thread can land — the effect re-runs once the node renders. If nothing
    // is active and it's still not visible, the linked record isn't loaded;
    // give up quietly.
    const visible = filtered.some((n) => n.id === focusId);
    if (!visible) {
      if (filters.size > 0) { setFilters(new Set()); return; }
      if (search) { setSearch(''); return; }
      setFocusId(null);
      return;
    }
    const el = (scrollRef.current || document).querySelector(`[data-node-id="${focusId}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('tl-pulse');
      const t = setTimeout(() => { el.classList.remove('tl-pulse'); setFocusId(null); }, 2200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setFocusId(null), 400);
    return () => clearTimeout(t);
  }, [focusId, filtered, filters, search]);

  const toggle = (id: string) => setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleFilter = (k: NodeKind) => setFilters((prev) => { const next = new Set(prev); if (next.has(k)) next.delete(k); else next.add(k); return next; });

  const renderNode = (n: TimelineNode) => {
    switch (n.kind) {
      case 'job':
        return <JobHero job={n.job!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} onFocusNode={focusNode}
          links={{
            parentFaultId: n.job!.faultId || n.job!.briefingHistory?.faultId,
            parentFaultLabel: (n.job!.faultId || n.job!.briefingHistory?.faultId) ? faultLabelById.get((n.job!.faultId || n.job!.briefingHistory?.faultId)!) : undefined,
            parentDealId: n.job!.dealId || undefined,
            parentDealLabel: n.job!.dealId ? dealLabelById.get(n.job!.dealId) : undefined,
          }}
          fullscreen={fullscreen} onNavigate={handleNavigate} team={team} />;
      case 'fault':
        return <FaultCard fault={n.fault!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} onFocusNode={focusNode}
          childJobs={n.fault!.id ? (jobsByFault.get(n.fault!.id) || []) : []} fullscreen={fullscreen} onNavigate={handleNavigate} />;
      case 'deal':
        return <DealCard deal={n.deal!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} onFocusNode={focusNode}
          childJobs={n.deal!.id ? (jobsByDeal.get(n.deal!.id) || []) : []} onNavigate={handleNavigate} />;
      case 'service':
        return <ServiceCard service={n.service!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} />;
      case 'service_request':
        return <ServiceRequestCard request={n.serviceRequest!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} onNavigate={handleNavigate} />;
      case 'call':
        return <CallCard call={n.call!} expanded={expanded.has(n.id)} onToggle={() => toggle(n.id)} />;
      case 'note':
        return (
          <NoteCard
            note={n.note!}
            replies={n.noteReplies ?? []}
            availableUsers={mentionUsers}
          />
        );
      case 'lifecycle':
        return <LifecycleRow entry={n.lifecycle!} />;
    }
  };

  const dotFor = (n: TimelineNode) => n.kind === 'job' ? jobMeta(n.job!.type).dot : KIND_META[n.kind].dot;

  return (
    <div className="flex flex-col gap-3">
      {/* Controls */}
      <div className="space-y-2.5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search this customer's history…"
            className="w-full pl-9 pr-9 py-2 bg-surface-glass border border-theme rounded-lg text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-brand-secondary/30 focus:border-brand-secondary/40"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-theme-muted hover:text-theme-primary hover:bg-surface-hover">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setFilters(new Set())}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
              filters.size === 0 ? 'bg-brand-secondary/20 text-brand-secondary border-brand-secondary/40' : 'bg-surface-hover text-theme-secondary border-theme hover:bg-surface-glass'
            }`}
          >
            All
          </button>
          {(['job', 'fault', 'deal', 'service', 'service_request', 'call', 'note'] as NodeKind[]).map((k) => {
            const active = filters.has(k);
            const Icon = KIND_META[k].icon;
            if (counts[k] === 0) return null;
            return (
              <button key={k} onClick={() => toggleFilter(k)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  active ? KIND_META[k].chipActive : 'bg-surface-hover text-theme-secondary border-theme hover:bg-surface-glass'
                }`}>
                <Icon className={`w-3 h-3 ${active ? '' : KIND_META[k].text}`} />
                {KIND_META[k].plural}
                <span className="tabular-nums text-[10px] opacity-70">{counts[k]}</span>
              </button>
            );
          })}
          {counts.lifecycle > 0 && (
            <button onClick={() => setShowLifecycle((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ml-auto ${
                showLifecycle ? KIND_META.lifecycle.chipActive : 'bg-surface-hover text-theme-muted border-theme hover:bg-surface-glass'
              }`}
              title="Show low-level system events (created / stage changes)">
              <History className="w-3 h-3" />
              {showLifecycle ? 'Hide system events' : 'System events'}
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div ref={scrollRef} className={fullscreen ? 'max-w-3xl mx-auto w-full' : ''}>
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-theme-muted">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-semibold text-theme-secondary">
              {nodes.length === 0 ? 'No history yet' : 'Nothing matches'}
            </p>
            <p className="text-xs mt-1">
              {nodes.length === 0 ? 'Jobs, faults, deals, services and calls will appear here.' : 'Try a different search or clear the filters.'}
            </p>
            {nodes.length > 0 && (search || filters.size > 0) && (
              <button onClick={() => { setSearch(''); setFilters(new Set()); }} className="mt-3 text-xs text-brand-secondary hover:underline">Clear filters</button>
            )}
            {nodes.length === 0 && emptyStateAction && (
              <div className="mt-4 flex justify-center">{emptyStateAction}</div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => (
              <div key={group.label}>
                {/* Month header */}
                <div className="flex items-center gap-3 mb-2.5 sticky top-0 z-10 py-1 bg-surface-card/80 backdrop-blur-sm">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-theme-muted">{group.label}</span>
                  <div className="flex-1 h-px bg-theme-soft" />
                  <span className="text-[10px] text-theme-muted tabular-nums">{group.nodes.length}</span>
                </div>
                {/* Spine + nodes */}
                <div className="relative pl-7">
                  <div className="absolute left-[10px] top-1 bottom-1 w-px bg-theme-soft" />
                  <div className="space-y-2.5">
                    {group.nodes.map((n) => (
                      <div key={n.id} data-node-id={n.id} className="relative rounded-2xl">
                        {/* Dot */}
                        <div
                          className={`absolute -left-[22px] rounded-full ring-4 ring-surface-card ${dotFor(n)} ${
                            n.tier === 'hero' ? 'w-3.5 h-3.5 top-3.5' : n.tier === 'tiny' ? 'w-2 h-2 top-2' : 'w-3 h-3 top-3'
                          }`}
                        />
                        {fullscreen && (
                          <span className="absolute -left-[88px] top-2.5 text-[9px] text-theme-muted tabular-nums hidden xl:block w-[60px] text-right">
                            {n.tier !== 'tiny' ? timeAgo(n.ms) : ''}
                          </span>
                        )}
                        {renderNode(n)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <div className="h-2" />
          </div>
        )}
      </div>
    </div>
  );
}
